/**
 * Native-shell glue. No-ops on the web; wires Capacitor plugins when the
 * app runs inside the Android AAB (or later, the iOS Xcode build).
 *
 * Called once at app boot from main.tsx AFTER Firebase init and BEFORE React renders.
 * All plugin calls are guarded by Capacitor.isNativePlatform() so nothing throws
 * or ships extra weight on the web build.
 */

import { Capacitor } from "@capacitor/core";
import { App as CapApp, type URLOpenListenerEvent } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";

import { initAdsNative } from "./ads";
import { router } from "./router";

const isNative = Capacitor.isNativePlatform();

export async function initCapacitorGlue(): Promise<void> {
  if (!isNative) return;

  // Hardware Android back button — default is "exit app", which is wrong for
  // a multi-screen flow. Go back in history when we can, exit otherwise.
  CapApp.addListener("backButton", ({ canGoBack }) => {
    if (canGoBack) window.history.back();
    else CapApp.exitApp();
  });

  // Android App Links — an incoming URL like https://web.travelix.app/invite/TOKEN
  // reaches the app as an appUrlOpen event. Map it to the equivalent in-app
  // route so React Router shows the accept page instead of leaving the user
  // on the boot / dashboard screen. Path-only navigation keeps the SPA state
  // intact; the app never opens the URL in a browser.
  CapApp.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
    handleIncomingUrl(event.url);
  });

  // Sync the native status bar to the current theme.
  await syncStatusBarToTheme();

  // React to theme changes coming from the ThemeProvider (via a custom event
  // so this module stays decoupled from React).
  window.addEventListener("travelix:theme-changed", () => {
    void syncStatusBarToTheme();
  });

  // Boot AdMob (Android only for now). Non-blocking — the app renders even if
  // ads fail to initialize. Fire-and-forget so we don't delay the splash hide.
  void initAdsNative();

  // Hide the splash once the first paint has landed.
  // launchShowDuration in capacitor.config.ts is our upper bound.
  requestAnimationFrame(() => {
    // A double rAF lands after React's first commit.
    requestAnimationFrame(() => {
      void SplashScreen.hide({ fadeOutDuration: 200 });
    });
  });
}

/**
 * Route an incoming App Links URL into the SPA.
 *
 * We accept ANY host — the intent-filter already scopes what the OS routes
 * here (web.travelix.app + /invite/*) — but only navigate when the path is
 * meaningful. Unknown paths fall back to the dashboard rather than exiting
 * or dumping the user on a blank screen.
 */
function handleIncomingUrl(rawUrl: string): void {
  try {
    const url = new URL(rawUrl);
    const path = `${url.pathname}${url.search}${url.hash}` || "/";
    // React Router's imperative navigate; { replace: true } avoids stacking
    // a duplicate history entry when the launcher intent itself already
    // navigated to "/" as part of app cold-start.
    void router.navigate(path, { replace: true });
  } catch {
    /* malformed URL — nothing sensible to do */
  }
}

async function syncStatusBarToTheme(): Promise<void> {
  if (!isNative) return;
  const dark =
    document.documentElement.getAttribute("data-theme") === "dark" ||
    (document.documentElement.getAttribute("data-theme") === null &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  try {
    await StatusBar.setStyle({ style: dark ? Style.Light : Style.Dark });
    await StatusBar.setBackgroundColor({ color: dark ? "#141110" : "#FBF8F4" });
  } catch {
    /* older Android may block color changes on some builds — non-fatal */
  }
}
